import { useMemo } from "react";

export interface LineItem {
  id?: string;
  product_id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  line_total: number;
}

export const useEstimateCalculations = (lineItems: LineItem[], taxRate: number = 8.625) => {
  const calculations = useMemo(() => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.line_total, 0);
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      taxAmount: Number(taxAmount.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  }, [lineItems, taxRate]);

  const calculateSquaresFromArea = (areaSqft: number): number => {
    return Number((areaSqft / 100).toFixed(2));
  };

  const calculateLineTotal = (
    quantity: number,
    unitPrice: number,
    discountPercent: number = 0
  ) => {
    const subtotal = quantity * unitPrice;
    const discountAmount = subtotal * (discountPercent / 100);
    const lineTotal = subtotal - discountAmount;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      discountAmount: Number(discountAmount.toFixed(2)),
      lineTotal: Number(lineTotal.toFixed(2)),
    };
  };

  const calculateSquaresNeeded = (areaSqft: number, wastePercent: number = 10) => {
    const squares = areaSqft / 100;
    const withWaste = squares * (1 + wastePercent / 100);
    return Number(withWaste.toFixed(2));
  };

  const calculateBundlesNeeded = (squares: number, bundlesPerSquare: number = 3) => {
    return Math.ceil(squares * bundlesPerSquare);
  };

  return {
    ...calculations,
    calculateLineTotal,
    calculateSquaresNeeded,
    calculateBundlesNeeded,
    calculateSquaresFromArea,
  };
};
